<template>
  <b-card
    :title="this.translations.editPointTier[lang]"
    @cancel="$emit('close')"
    @submit="submitDialog"
    :cancel-text="{ el: 'Κλείσιμο', en: 'Close', it: 'Chiudere' }"
    :error-message="errorMessage"
  >
    <b-text-field
      v-model="tierData.title[titleLang]"
      :label="translations.title[lang]"
      class="mb-4"
      :success="success.title[tierIndex]"
      :error-messages="error.title[tierIndex]"
      @focus="error.title[tierIndex] = ''"
      @blur="validateTitle({ i: tierIndex, tierData })"
    >
      <template v-slot:append>
        <b-lang-menu v-model="titleLang" type="inner"></b-lang-menu>
      </template>
    </b-text-field>
    <v-textarea
      v-model="tierData.description[descLang]"
      :label="translations.description[lang]"
      outlined
      clearable
      no-resize
      hide-details
      :success="success.description[tierIndex]"
      :error-messages="error.description[tierIndex]"
      @focus="error.description[tierIndex] = ''"
      @blur="validateDescription({ i: tierIndex, tierData })"
    >
      <template v-slot:append>
        <b-lang-menu v-model="descLang" type="inner"></b-lang-menu>
      </template>
    </v-textarea>
    <v-row v-if="tiers_settings.system === 'expiration_mode'" class="mt-1 pt-1">
      <v-col v-if="tierIndex > 0" cols="12" md="6" class="d-flex flex-column pt-0">
        <b-text-field
          v-model="tierData.expiration_days"
          type="number"
          :label="translations.expirationDays[lang]"
          no-margin-top
          :success="success.expiration_days[tierIndex]"
          :error-messages="error.expiration_days[tierIndex]"
          @focus="error.expiration_days[tierIndex] = ''"
          @blur="validateExpirationDays({ i: tierIndex, tierData })"
        ></b-text-field>
        <v-alert dense outlined type="warning" class="my-1 pb-2">
          <span class="exp-days-description mb-n3">{{
            translations.expirationDaysDescription[lang]
          }}</span>
        </v-alert>
      </v-col>
      <v-col v-if="tierIndex" cols="12" md="6" class="d-flex flex-column pt-0">
        <b-text-field
          v-model="tierData.valid_days_before"
          :label="translations.countableDays[lang]"
          no-margin-top
          :success="success.valid_days_before[tierIndex]"
          :error-messages="error.valid_days_before[tierIndex]"
          @focus="error.valid_days_before[tierIndex] = ''"
          @blur="validateCountableDays({ i: tierIndex, tierData })"
        ></b-text-field>
        <span class="exp-days-description mb-n3">{{
          translations.countableDaysDescription[lang]
        }}</span>
      </v-col>
    </v-row>

    <div class="color-picker-container mt-8">
      <span class="color-picker-label">{{ translations.color[lang] }}:</span>
      <v-color-picker
        v-model="tierData.color"
        class="mt-4 tier-color-picker"
        :canvas-height="200"
        :width="400"
        mode="hexa"
        style="
          max-width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          flex-direction: column;
        "
      ></v-color-picker>
    </div>
  </b-card>
</template>

<script>
import { mapMutations, mapState, mapActions } from 'vuex';
import translations from '@/utils/translations/loyaltyPanel/pointTitles';
import validators from '@/utils/validators/loyaltyPanel/pointTiers';
import { cloneDeep, isEqual } from 'lodash';

export default {
  name: 'PointTierForm',

  mixins: [translations, validators],

  props: {
    tier: Object,
    tierIndex: Number,
    infinityMultiplier: Number,
    isOpen: Boolean,
  },

  data() {
    return {
      icons: {},
      titleLang: this.$route.params.lang,
      descLang: this.$route.params.lang,
      changeTitleElAuto: true,
      changeTitleItAuto: true,
      changeDescriptionElAuto: true,
      changeDescriptionItAuto: true,
      changeTitleEnAuto: true,
      changeDescriptionEnAuto: true,
      tierData: {},
    };
  },
  computed: {
    ...mapState(['loading', 'errorMessage']),
    ...mapState('loyaltyPanel', ['tiers_settings']),
    ...mapState('loyaltyPanel/pointTitles', ['tiers', 'tierValid']),

    lang() {
      return this.$route.params.lang;
    },
    title() {
      return this.mode === 1
        ? this.translations.addPointPackage[this.lang]
        : this.translations.updatePointPackage[this.lang];
    },
  },
  watch: {
    tier: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val && !isEqual(val, this.tierData)) {
          this.tierData = cloneDeep(val);
        }
      },
    },
    ['tierData.color'](val, oldVal) {
      let newVal = [...val];
      let l = val.length;
      let ol = oldVal.length;
      if (oldVal[ol - 1] !== 'F' && oldVal[ol - 2] !== 'F' && val[0] === '#' && l === 9) {
        newVal[l - 1] = 'F';
        newVal[l - 2] = 'F';
        this.tierData.color = newVal.join();
      }
    },

    ['tierData.title.en'](val) {
      if (this.lang == 'en') {
        if (this.changeTitleElAuto) {
          this.tierData.title.el = val;
        }

        if (this.changeTitleItAuto) {
          this.tierData.title.it = val;
        }
        this.changeNameEnAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleEnAuto = false;
      }
    },

    ['tierData.title.el'](val) {
      if (this.lang == 'el') {
        if (this.changeTitleEnAuto) {
          this.tierData.title.en = val;
        }

        if (this.changeTitleItAuto) {
          this.tierData.title.it = val;
        }
        this.changeNameElAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleElAuto = false;
      }
    },

    ['tierData.title.it'](val) {
      if (this.lang == 'it') {
        if (this.changeTitleEnAuto) {
          this.tierData.title.en = val;
        }

        if (this.changeTitleElAuto) {
          this.tierData.title.el = val;
        }
        this.changeTitleItAuto = false;
      } else if (val != this.tierData.title[this.lang]) {
        this.changeTitleItAuto = false;
      }
    },

    ['tierData.description.en'](val) {
      if (this.lang == 'en') {
        if (this.changeDescriptionElAuto) {
          this.tierData.description.el = val;
        }

        if (this.changeDescriptionItAuto) {
          this.tierData.description.it = val;
        }
        this.changeDescriptionEnAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionEnAuto = false;
      }
    },

    ['tierData.description.el'](val) {
      if (this.lang == 'el') {
        if (this.changeDescriptionEnAuto) {
          this.tierData.description.en = val;
        }

        if (this.changeDescriptionItAuto) {
          this.tierData.description.it = val;
        }
        this.changeDescriptionElAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionElAuto = false;
      }
    },

    ['tierData.description.it'](val) {
      if (this.lang == 'it') {
        if (this.changeDescriptionEnAuto) {
          this.tierData.description.en = val;
        }

        if (this.changeDescriptionElAuto) {
          this.tierData.description.el = val;
        }
        this.changeDescriptionItAuto = false;
      } else if (val != this.tierData.description[this.lang]) {
        this.changeDescriptionItAuto = false;
      }
    },

    ['$route.params.lang'](val) {
      this.titleLang = val;
      this.descLang = val;
    },

    formValid: {
      handler(val, oldVal) {
        this.setTierValid(val);
      },
    },

    isOpen(val) {
      if (val) {
        this.validateTierForm(this.tierIndex, this.tier);
      }
    },
  },

  mounted() {
    this.resetValidation();
    this.validateTierForm();
    this.setTierValid(this.formValid);
  },

  methods: {
    ...mapMutations('loyaltyPanel/pointTitles', ['setTierValid']),

    submitDialog() {
      try {
        console.log('submitDialog', this.tierData, this.tierIndex);
        this.$emit('submit', { tier: this.tierData, i: this.tierIndex });
      } catch (error) {
        console.log('error', error);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.color-picker-container {
  max-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  border: 1px solid gray;
  padding: 5px;
  margin-top: 15px;
  border-radius: 5px;
  flex-direction: column;
  align-items: flex-start;
}
.color-picker-label {
  margin-top: -19px;
  background: white;
  padding: 2px;
  margin-left: 3px;
}
.exp-days-description {
  padding: 3px 15px 0px 10px;
  font-style: italic;
}
</style>

<style>
.tier-color-picker {
  max-width: 100% !important;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100% !important;
}
.tier-color-picker .v-color-picker__alpha {
  display: none;
}
.tier-color-picker .v-color-picker__canvas {
  width: 50% !important;
  min-width: 400px;
}
/* .tier-color-picker .v-color-picker__canvas canvas{
    width: 100% !important;
    height: 100% !important;
    min-width: 100% !important;
} */
.tier-color-picker .v-color-picker__controls {
  width: 100% !important;
}
.hidden {
  visibility: hidden;
}
</style>
